import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const LS_KEY = "metamorph_site_token";

/**
 * SiteGate — wraps the public site behind a single password during the 24h
 * VIP preview window. Activates only when SITE_PASSWORD is set in backend env.
 * Admin routes (/admin/*) and Stripe redirect routes bypass the gate.
 */
export default function SiteGate({ children }) {
  const [locked, setLocked] = useState(null); // null = checking, true = needs pwd, false = open
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Admin routes and success/cancel pages bypass the gate entirely
  const bypass = typeof window !== "undefined" && (
    window.location.pathname.startsWith("/admin") ||
    window.location.pathname.startsWith("/success") ||
    window.location.pathname.startsWith("/cancel")
  );

  useEffect(() => {
    const t = setTimeout(() => {
      if (bypass) { setLocked(false); return; }
      const stored = (() => { try { return localStorage.getItem(LS_KEY); } catch { return null; } })();
      api.get("/site/access").then(async ({ data }) => {
        if (!data?.locked) { setLocked(false); return; }
        // Validate the stored token against the current SITE_PASSWORD. If the
        // password was rotated (or never matched), the user gets the gate.
        if (!stored) { setLocked(true); return; }
        try {
          const { data: v } = await api.post("/site/access/verify", { token: stored });
          if (v?.ok) { setLocked(false); }
          else { try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ } setLocked(true); }
        } catch { setLocked(true); }
      }).catch(() => setLocked(false));
    }, 0);
    return () => clearTimeout(t);
  }, [bypass]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/site/access", { password: password.trim() });
      if (data?.ok && data.token) {
        try { localStorage.setItem(LS_KEY, data.token); } catch { /* ignore */ }
        setLocked(false);
      } else {
        setError("Wrong password.");
      }
    } catch { setError("Couldn't reach the server."); }
    finally { setSubmitting(false); }
  };

  if (locked === null) {
    // Brief checking flash — render nothing rather than flashing the gate
    return null;
  }
  if (locked === false) return children;

  return (
    <main className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 py-10" data-testid="site-gate">
      <div className="w-full max-w-sm text-center">
        <h1 className="vintage-text text-5xl sm:text-6xl mb-3" data-testid="site-gate-title">Metamorph</h1>
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] opacity-65 mb-10">
          Private preview · early access
        </p>
        <form onSubmit={onSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="line-input text-center"
            autoFocus
            data-testid="site-gate-input"
          />
          <button
            type="submit"
            disabled={submitting || !password.trim()}
            className="btn-line btn-filled w-full justify-center mt-6 disabled:opacity-30"
            data-testid="site-gate-submit"
          >
            {submitting ? "Checking…" : "Enter"}
          </button>
          {error && <p className="text-rose-300/80 text-xs mt-4 font-mono">{error}</p>}
        </form>
      </div>
    </main>
  );
}
